'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

// Backend integration point: replace with API call to /api/properties?featured=true
const featuredProperties = [
{
  id: 'prop-001',
  title: 'Saadiyat Beach Villa',
  location: 'Saadiyat Island, Abu Dhabi',
  price: 18500000,
  beds: 6,
  baths: 7,
  area: 9800,
  type: 'Villa',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_124a18683-1772153562253.png",
  imageAlt: 'Luxury white villa with infinity pool and manicured garden in Saadiyat Island Abu Dhabi',
  badges: ['luxury', 'verified'],
  agent: 'Khalid Al-Rashid',
  agency: 'OROW Premium',
  pricePerSqft: 1888,
  yield: 4.2
},
{
  id: 'prop-002',
  title: 'Palm Signature Villa',
  location: 'Palm Jumeirah, Dubai',
  price: 32000000,
  beds: 7,
  baths: 8,
  area: 14200,
  type: 'Villa',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1217475fc-1772167243094.png",
  imageAlt: 'Modern luxury villa with private pool and palm trees on Palm Jumeirah Dubai',
  badges: ['luxury', 'featured', 'verified'],
  agent: 'Nadia Petrov',
  agency: 'Elite Estates Dubai',
  pricePerSqft: 2254,
  yield: 3.8
},
{
  id: 'prop-003',
  title: 'Dubai Marina Penthouse',
  location: 'Dubai Marina, Dubai',
  price: 12500000,
  beds: 4,
  baths: 5,
  area: 5400,
  type: 'Penthouse',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_135f99131-1772210224999.png",
  imageAlt: 'Luxury penthouse interior with floor-to-ceiling windows overlooking Dubai Marina skyline',
  badges: ['featured', 'verified'],
  agent: 'Omar Hassan',
  agency: 'Marina Realty',
  pricePerSqft: 2315,
  yield: 5.1
},
{
  id: 'prop-004',
  title: 'Yas Bay Waterfront Apartment',
  location: 'Yas Island, Abu Dhabi',
  price: 2950000,
  beds: 2,
  baths: 3,
  area: 1480,
  type: 'Apartment',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_16b477bb8-1772210228680.png",
  imageAlt: 'Modern apartment living room with waterfront views and contemporary furniture in Abu Dhabi',
  badges: ['new', 'verified'],
  agent: 'Fatima Al-Zahra',
  agency: 'Yas Homes',
  pricePerSqft: 1993,
  yield: 6.2
},
{
  id: 'prop-005',
  title: 'Al Reem Sky Residence',
  location: 'Al Reem Island, Abu Dhabi',
  price: 1850000,
  beds: 1,
  baths: 2,
  area: 920,
  type: 'Apartment',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1105cb397-1773044075748.png",
  imageAlt: 'Bright modern apartment bedroom with city skyline view in Al Reem Island Abu Dhabi',
  badges: ['verified'],
  agent: 'Ahmed Mansoor',
  agency: 'Reem Properties',
  pricePerSqft: 2011,
  yield: 7.1
},
{
  id: 'prop-006',
  title: 'Downtown Dubai Sky Tower',
  location: 'Downtown Dubai, Dubai',
  price: 4200000,
  beds: 3,
  baths: 4,
  area: 2100,
  type: 'Apartment',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b0f0b158-1772110248557.png",
  imageAlt: 'Luxury apartment with Burj Khalifa view and open plan kitchen in Downtown Dubai',
  badges: ['featured', 'verified'],
  agent: 'Sara Al-Mansoori',
  agency: 'Downtown Realty',
  pricePerSqft: 2000,
  yield: 5.8
}];


function formatPrice(price: number) {
  if (price >= 1000000) return `AED ${(price / 1000000).toFixed(1)}M`;
  return `AED ${(price / 1000).toFixed(0)}K`;
}

const badgeMap: Record<string, {label: string;className: string;}> = {
  luxury: { label: 'Luxury', className: 'badge-luxury' },
  verified: { label: '✓ Verified', className: 'badge-verified' },
  featured: { label: 'Featured', className: 'badge-featured' },
  new: { label: 'New', className: 'badge-new' },
  offplan: { label: 'Off-Plan', className: 'badge-offplan' }
};

export default function FeaturedProperties() {
  const [saved, setSaved] = useState<Set<string>>(new Set());

  const toggleSave = (id: string) => {
    setSaved((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else
      next.add(id);
      return next;
    });
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-accent text-sm font-semibold uppercase tracking-widest mb-2">Handpicked for You</p>
            <h2 className="section-title text-3xl">Featured Properties</h2>
            <p className="section-subtitle mt-1">Curated luxury listings verified by OROW experts</p>
          </div>
          <Link
            href="/property-listings-page"
            className="hidden md:flex items-center gap-2 text-primary font-semibold text-sm hover:gap-3 transition-all duration-200">
            
            View All Listings
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-6">
          {featuredProperties.map((prop) =>
          <Link
            key={prop.id}
            href="/property-details-page"
            className="card-base group cursor-pointer block">
            
              {/* Image */}
              <div className="relative overflow-hidden h-56">
                <AppImage
                src={prop.image}
                alt={prop.imageAlt}
                fill
                className="object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw" />
              
                <div className="absolute inset-0 bg-card-overlay" />

                {/* Badges */}
                <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
                  {prop.badges.map((badge) =>
                <span key={`${prop.id}-badge-${badge}`} className={badgeMap[badge]?.className ?? 'badge-verified'}>
                      {badgeMap[badge]?.label ?? badge}
                    </span>
                )}
                </div>

                {/* Save */}
                <button
                onClick={(e) => {e.preventDefault();toggleSave(prop.id);}}
                className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${
                saved.has(prop.id) ?
                'bg-accent text-white' : 'bg-black/30 text-white hover:bg-black/50'}`
                }
                aria-label="Save property">
                
                  <Icon name={saved.has(prop.id) ? 'HeartIconSolid' : 'HeartIcon'} size={16} />
                </button>

                {/* Price overlay */}
                <div className="absolute bottom-3 left-3">
                  <div className="text-white font-extrabold text-xl font-tabular">{formatPrice(prop.price)}</div>
                  <div className="text-white/70 text-xs">AED {prop.pricePerSqft.toLocaleString()}/sqft</div>
                </div>

                {/* Yield */}
                <div className="absolute bottom-3 right-3 bg-primary/90 backdrop-blur-sm text-white text-xs font-bold px-2 py-1 rounded-lg">
                  {prop.yield}% Yield
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="font-bold text-foreground text-base mb-1 line-clamp-1">{prop.title}</h3>
                <div className="flex items-center gap-1 text-muted-foreground text-sm mb-4">
                  <Icon name="MapPinIcon" size={13} />
                  <span className="line-clamp-1">{prop.location}</span>
                </div>

                {/* Specs */}
                <div className="flex items-center gap-4 text-sm text-foreground/80 mb-4 pb-4 border-b border-border">
                  <div className="flex items-center gap-1.5">
                    <Icon name="HomeIcon" size={14} className="text-primary" />
                    <span className="font-medium">{prop.beds} Beds</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-border">|</span>
                    <span className="font-medium">{prop.baths} Baths</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-border">|</span>
                    <span className="font-medium">{prop.area.toLocaleString()} sqft</span>
                  </div>
                </div>

                {/* Agent & Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
                      <Icon name="UserIcon" size={14} className="text-primary" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-foreground">{prop.agent}</div>
                      <div className="text-xs text-muted-foreground">{prop.agency}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                    onClick={(e) => e.preventDefault()}
                    className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center hover:bg-primary hover:text-white text-primary transition-all duration-200"
                    aria-label="Call agent">
                    
                      <Icon name="PhoneIcon" size={14} />
                    </button>
                    <button
                    onClick={(e) => e.preventDefault()}
                    className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center hover:bg-green-500 hover:text-white text-green-700 transition-all duration-200"
                    aria-label="WhatsApp agent">
                    
                      <Icon name="ChatBubbleLeftIcon" size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </Link>
          )}
        </div>

        <div className="mt-8 text-center md:hidden">
          <Link href="/property-listings-page" className="btn-outline">
            View All Listings
          </Link>
        </div>
      </div>
    </section>);

}